'use client';

import { useState, useCallback, useEffect } from 'react';
import { useIsMobile } from '@/core/hooks/useMediaQuery';
import { useToast } from '@/core/hooks/useToast';
import { useStaffCallStore } from '../hooks/useStaffCallClient';
import {
  useStaffSignalingConnection,
  useSignalingConnection,
} from '../hooks/useSignalingConnection';
import { IncomingCallPanelDesktop } from './IncomingCallPanelDesktop';
import { IncomingCallModalMobile } from './IncomingCallModalMobile';
import { ActiveCallPanel } from './ActiveCallPanel';
import { TransferDialog } from './TransferDialog';
import { CallsCenterFloating } from './CallsCenterFloating';
import { startRingtone, stopRingtone } from '../lib/ringtone';

export function IncomingCallHost() {
  const isMobile = useIsMobile();
  const store = useStaffCallStore();
  const [showTransfer, setShowTransfer] = useState(false);
  const signalingAuth = useStaffSignalingConnection();
  const toast = useToast();

  // Surface missed calls as a toast. The gateway has always broadcast
  // `call:missed-new` to the whole property and nothing rendered it, so a call
  // that rang out left no trace on any screen — the front desk only found out
  // if someone thought to open call history.
  const handleMissedCall = useCallback(
    (info: { callId: string; callerName?: string; bookingId?: string }) => {
      toast.info(
        'Missed call',
        info.bookingId
          ? `${info.callerName ?? 'A guest'} — booking ${info.bookingId}`
          : `${info.callerName ?? 'A guest'} called and nobody answered`,
      );
    },
    [toast],
  );

  // Tell the staff member the desk is reachable again. Between the drop and
  // this, no incoming call could have reached them at all — silence is not a
  // safe thing to leave unexplained at a front desk.
  const handleReconnected = useCallback(() => {
    toast.success('Reconnected', 'You are receiving calls again.');

    // Rebuild the media session for a call that is STILL LIVE.
    //
    // A reconnect restores signalling only — the mediasoup transports were
    // bound to the socket that died. Without this the staff member is
    // reconnected, the panel says "connected", and neither side can hear
    // anything: the failure is completely invisible, which is the worst shape
    // it can take. No-op unless a call is actually up.
    useStaffCallStore.getState().restoreMediaAfterReconnect();
  }, [toast]);

  useSignalingConnection(signalingAuth, handleMissedCall, handleReconnected);

  useEffect(() => {
    if (store.state === 'ended') {
      store.reset();
    }
  }, [store, store.state]);

  // Report a failed or refused OUTBOUND call as a toast.
  //
  // `setOutboundFailed` records the reason — "guest is not online", "the guest
  // declined", "no answer" — and nothing rendered it, so the staff member's
  // panel simply closed. Each of these changes what they do next (phone the
  // number on the booking, try later, try a colleague), which is only useful
  // if they can read it. Cleared immediately so the same message cannot
  // reappear on the next render.
  useEffect(() => {
    if (!store.outboundError) return;
    toast.info('Call not connected', store.outboundError);
    store.clearOutboundError();
  }, [store, store.outboundError, toast]);

  // RING AUDIBLY while a call is incoming.
  //
  // Push notifications only fire when the tab is BACKGROUNDED, so a staff
  // member with the app open and focused — the normal state at a front desk —
  // heard nothing at all. The panel appeared silently and was missed by anyone
  // looking at another part of the same screen.
  //
  // Keyed on `incomingCall?.callId` as well as the state so a second call
  // arriving right after the first re-arms the ring rather than staying silent
  // because the state never left 'incoming'.
  useEffect(() => {
    if (store.state === 'incoming' && store.incomingCall) {
      startRingtone();
    } else {
      stopRingtone();
    }
    // Stop on unmount too: navigating away mid-ring would otherwise leave a
    // ringtone scheduled up to a minute ahead with nothing left to stop it.
    return stopRingtone;
  }, [store.state, store.incomingCall?.callId, store.incomingCall]);

  // The signalling auth IS the staff identity — the server derived it from the
  // JWT. The previous fallback chain read `activeCall.staffId` (null before a
  // call is answered) then `incomingCall.origin` (the literal string 'socket'
  // or 'push'), so every accept recorded the answering staff member as
  // "socket" or "unknown": `answeredByStaffId` was wrong on every call row,
  // and per-staff call attribution was meaningless.
  const staffId = signalingAuth?.userId ?? '';

  const handleAccept = useCallback(() => {
    if (!staffId) return;
    store.acceptCall(staffId);
  }, [store, staffId]);

  const handleDecline = useCallback(() => {
    store.declineCall();
  }, [store]);

  // Tell the gateway before tearing down locally.
  //
  // `endCall()` only closes the local media session. Without the emit the
  // server keeps the room open, holds its capacity slot and room claim, never
  // banks the duration, and leaves the guest connected to silence until the
  // ring deadline eventually reaps it.
  const handleHangup = useCallback(() => {
    const callId = store.activeCall?.callId;
    if (callId && store.socketEmit) {
      store.socketEmit('call:hangup', { callId }, () => {});
    }
    store.endCall();
  }, [store]);

  // Transfer is a request, not a local action.
  //
  // This used to close the panel and drop the call locally without telling
  // anyone — the target was never rung and the guest was cut off. The call
  // stays on screen until the gateway confirms with `call:transferred`; if the
  // target is unavailable it replies `call:transfer-failed` and the call
  // remains ours.
  const handleTransfer = useCallback(
    (toStaffId: string) => {
      const callId = store.activeCall?.callId;
      setShowTransfer(false);
      // Clear any previous failure before asking again, or a retry shows the
      // old reason until the gateway replies.
      store.clearTransferError();
      if (!callId || !store.socketEmit) return;
      store.socketEmit('call:transfer', { callId, toStaffId }, () => {});
    },
    [store]
  );

  const isIncoming = store.state === 'incoming';
  const isActive =
    store.state === 'connected' ||
    store.state === 'connected-muted' ||
    store.state === 'connecting';

  return (
    <>
      {isIncoming && !isMobile && (
        <IncomingCallPanelDesktop
          onAccept={handleAccept}
          onDecline={handleDecline}
        />
      )}

      {isIncoming && isMobile && (
        <IncomingCallModalMobile
          onAccept={handleAccept}
          onDecline={handleDecline}
        />
      )}

      {isActive && (
        <ActiveCallPanel
          onHangup={handleHangup}
          onTransfer={() => setShowTransfer(true)}
        />
      )}

      <TransferDialog
        open={showTransfer}
        onClose={() => setShowTransfer(false)}
        onTransfer={handleTransfer}
        currentStaffId={staffId}
      />

      {/*
        Mounted here rather than on the dashboard because this host is rendered
        by CallProvider on every PMS route and already holds the authenticated
        staff identity. A call ringing an occupied colleague is claimable from
        wherever the user happens to be, not only if they are looking at the
        dashboard. Renders nothing when nothing is ringing and the queue is
        empty.
      */}
      {staffId && <CallsCenterFloating staffId={staffId} />}
    </>
  );
}
